import { apiFetch } from '@/lib/api';
import { queryClient } from '@/lib/query-client';
import { useAuthStore } from '@/stores/auth-store';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { routeTree } from './routeTree.gen';
import './index.css';

if (!import.meta.env.PROD) {
  import('eruda').then((m) => m.default.init());
}

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

/**
 * Bootstrap: if there is a persisted token, validate it against /auth/me.
 * On failure (401 or network error), clear the store so the user is sent to /login.
 */
async function bootstrap() {
  const { token, clear } = useAuthStore.getState();
  if (token) {
    try {
      await apiFetch('/api/auth/me');
    } catch {
      clear();
    }
  }
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('root element missing');

bootstrap().then(() => {
  createRoot(rootEl).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  );
});
